import * as React from 'react';
import classnames from 'classnames';
import { sortBy } from 'lodash';

import DeliverableMediaForInsights from './media-for-insights/DeliverableMediaForInsights';
import DeliverableMediaAttachmentForApproval from './DeliverableMediaAttachmentForApproval';
import DeliverableMediaBlank from './DeliverableMediaBlank';
import { withModal } from 'components/modal/ModalHOC';

import { DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_CONTENT,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { t } from 'utils/language';

import type { Deliverable, DeliverableAttachment, DeliverableAttachmentApprovalStatus, DeliverableAttachmentType, Subcampaign } from 'types';
import type { RenderReplacementUploader } from './media-for-insights/RenderReplacementUploader.type';

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

type Props = {
  isNewDeliverable?: boolean,
  canApprove?: boolean,
  canUpload: boolean,
  subcampaign?: Subcampaign,
  deliverable: Deliverable,
  placeholderForUpload?: React.ReactNode | null,
  renderReplacementUploader?: RenderReplacementUploader,
  attachments: Array<DeliverableAttachment>,
  mode?: DeliverableAttachmentType,
  allowContentMove?: boolean,
  onApprovalStatusChanged?: (deliverableAttachment: DeliverableAttachment, approvalStatus: DeliverableAttachmentApprovalStatus) => void,
  onResetApprovalStatus?: (deliverableAttachments: Array<DeliverableAttachment>) => void,
  onDeleteAttachments?: (deliverableAttachments: Array<DeliverableAttachment>) => void,
  onReplaceAttachment?: (replacement: DeliverableAttachment, current: DeliverableAttachment) => void,
  onOpenClientApproval?: (openClientApproval?: boolean) => void,
  onEditMode?: () => void,
  onOpenMoveContent?: (showMoveContent: boolean) => void,
  onApplyClientFeedback?: (deliverable: Deliverable, attachmentId?: number | null) => void
};

class DeliverableMediaAttachmentsForDeliverable extends React.PureComponent<Props> {
  static defaultProps = {
    mode: 'content'
  };

  render(): React.ReactNode {
    const {
      isNewDeliverable,
      mode,
      attachments,
      canUpload,
      canApprove,
      subcampaign,
      deliverable,
      placeholderForUpload,
      renderReplacementUploader,
      allowContentMove,
      onDeleteAttachments,
      onReplaceAttachment,
      onEditMode,
      onOpenMoveContent,
      onApprovalStatusChanged,
      onResetApprovalStatus,
      onOpenClientApproval,
      onApplyClientFeedback
    } = this.props;
    const sortedAttachments = sortBy(attachments, 'id');

    if (!canUpload && sortedAttachments.length === 0) {
      return (
        <DeliverableMediaBlank
          type="placeholder"
          message={t('deliverable_approval.blank_media_content_approval')}
        />
      );
    }

    const galleryItems = prepareDeliverableAttachmentsForGallery(sortedAttachments);

    const attachmentsClasses = classnames('deliverable-media-row__attachments', {
      'deliverable-media-row__attachments--for-approval': true
    });

    return (
      <div className={attachmentsClasses}>
        {
          sortedAttachments.map((attachment, index) => (
            <DivWithModal
              key={`deliverable-media-attachment-${attachment.id}`}
              modalContentProps={{
                items: galleryItems,
                currentPosition: index
              }}
            >
              {
                (mode === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS || mode === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES) && (
                  <DeliverableMediaForInsights
                    deliverable={deliverable}
                    attachment={attachment}
                    onEditModeToggled={onEditMode}
                    isEditMode={canUpload}
                    onDeleteAttachments={onDeleteAttachments}
                    onReplaceAttachment={onReplaceAttachment}
                    renderReplacementUploader={renderReplacementUploader}
                  />
                )
              }
              {
                mode === DELIVERABLE_ATTACHMENT_TYPE_CONTENT && onApprovalStatusChanged && subcampaign && (
                  <DeliverableMediaAttachmentForApproval
                    isNewDeliverable={isNewDeliverable}
                    onEditMode={onEditMode}
                    canUpload={canUpload}
                    canApprove={Boolean(canApprove)}
                    subcampaign={subcampaign}
                    deliverable={deliverable}
                    attachment={attachment}
                    allowContentMove={allowContentMove}
                    onOpenMoveContent={onOpenMoveContent}
                    onApprovalStatusChanged={onApprovalStatusChanged}
                    onResetApprovalStatus={onResetApprovalStatus}
                    onDeleteAttachments={onDeleteAttachments}
                    onOpenClientApproval={onOpenClientApproval}
                    onApplyClientFeedback={onApplyClientFeedback}
                  />
                )
              }
            </DivWithModal>
          ))
        }
        {canUpload && placeholderForUpload}
      </div>
    );
  }
}

export default DeliverableMediaAttachmentsForDeliverable;
